import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FullscreenFormDialog } from "@/components/ui/fullscreen-form-dialog";
import { CreateExpenseForm } from "@/components/accounting/CreateExpenseForm";
import { NumberDisplay } from "@/components/ui/number-display";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useShowAccountNumbers } from "@/hooks/useShowAccountNumbers";
import { CurrencyDisplay } from "@/components/ui/currency-display";

interface ExpenseLinkingSectionProps {
  selectedExpenseId?: string;
  selectedLineItemId?: string;
  onExpenseChange: (expenseId: string) => void;
  onLineItemChange: (lineItemId: string) => void;
  currentAssetId?: string; // If editing an asset, this is its ID (to allow keeping current link)
}

/**
 * ExpenseLinkingSection - Reusable component for linking expenses and line items
 *
 * Provides a stepped flow for selecting an expense and optionally a specific line item.
 * Includes ability to create new expenses inline.
 */
export function ExpenseLinkingSection({
  selectedExpenseId = "",
  selectedLineItemId = "",
  onExpenseChange,
  onLineItemChange,
  currentAssetId,
}: ExpenseLinkingSectionProps) {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const showAccountNumbers = useShowAccountNumbers();
  const [expenses, setExpenses] = useState<Array<{ id: string; expense_number: string; description: string; total_amount: number; expense_date: string }>>([]);
  const [lineItemsWithAccounts, setLineItemsWithAccounts] = useState<any[]>([]);
  const [linkedLineItems, setLinkedLineItems] = useState<Set<string>>(new Set());
  const [showCreateExpense, setShowCreateExpense] = useState(false);
  const [expenseSubmitHandler, setExpenseSubmitHandler] = useState<(() => void) | null>(null);

  // Fetch expenses when component mounts or organization changes
  useEffect(() => {
    if (selectedOrganization) {
      fetchExpenses();
    }
  }, [selectedOrganization]);

  // Fetch line items when expense is selected
  useEffect(() => {
    if (!selectedExpenseId) {
      setLineItemsWithAccounts([]);
      setLinkedLineItems(new Set());
      onLineItemChange("");
      return;
    }

    const fetchLineItems = async () => {
      // Fetch line items for this expense
      const { data, error } = await supabase
        .from('invoice_line_items')
        .select(`
          *,
          expense_account:chart_of_accounts!expense_account_id(
            id,
            account_name,
            account_number
          )
        `)
        .eq('expense_id', selectedExpenseId)
        .order('sort_order', { ascending: true });

      if (error || !data) return;

      setLineItemsWithAccounts(data);

      // Fetch which line items are already linked to other assets in this organization
      if (!selectedOrganization) return;

      let query = supabase
        .from('assets')
        .select('purchase_line_item_id')
        .eq('organization_id', selectedOrganization.id)
        .not('purchase_line_item_id', 'is', null)
        .in('purchase_line_item_id', data.map(item => item.id));

      // If editing an asset, exclude the current asset's link
      if (currentAssetId) {
        query = query.neq('id', currentAssetId);
      }

      const { data: linkedAssets, error: linkedError } = await query;

      if (!linkedError && linkedAssets) {
        const linkedIds = new Set(linkedAssets.map(a => a.purchase_line_item_id).filter(Boolean));
        setLinkedLineItems(linkedIds);

        // Auto-select if there's only one available (not linked) line item
        const availableItems = data.filter(item => !linkedIds.has(item.id));
        if (availableItems.length === 1) {
          onLineItemChange(availableItems[0].id);
        } else {
          onLineItemChange("");
        }
      } else {
        // If we can't fetch linked items, just auto-select single item as before
        if (data.length === 1) {
          onLineItemChange(data[0].id);
        } else {
          onLineItemChange("");
        }
      }
    };

    fetchLineItems();
  }, [selectedExpenseId, currentAssetId, selectedOrganization]);

  const fetchExpenses = async () => {
    if (!selectedOrganization) return;

    const { data, error } = await supabase
      .from("expenses")
      .select("id, expense_number, description, total_amount, expense_date")
      .eq("organization_id", selectedOrganization.id)
      .order("expense_date", { ascending: false })
      .limit(100);

    if (!error && data) {
      setExpenses(data);
    }
  };

  const handleExpenseCreated = () => {
    setShowCreateExpense(false);
    fetchExpenses();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {t("things.form.expenseLinking.description")}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowCreateExpense(true)}
          className="shrink-0"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("things.form.expenseLinking.createNewExpense")}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Step 1: Select Expense */}
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
              1
            </div>
            <label className="text-sm font-medium">{t("things.form.expenseLinking.selectExpense")}</label>
          </div>
          <div className="ml-8">
            <Select value={selectedExpenseId} onValueChange={onExpenseChange}>
              <SelectTrigger>
                <SelectValue placeholder={t("things.form.expenseLinking.chooseExpense")} />
              </SelectTrigger>
              <SelectContent>
                {expenses.map((expense) => (
                  <SelectItem key={expense.id} value={expense.id}>
                    {expense.expense_number} - {expense.description} (<CurrencyDisplay value={expense.total_amount} />)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Step 2: Select Line Item (conditional) */}
        {lineItemsWithAccounts.length > 0 && (
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                2
              </div>
              <label className="text-sm font-medium">
                {t("things.form.expenseLinking.selectLineItem")}
              </label>
            </div>
            <div className="ml-8">
              <p className="text-xs text-muted-foreground mb-2">
                {(() => {
                  const availableCount = lineItemsWithAccounts.filter(item => !linkedLineItems.has(item.id)).length;
                  const linkedCount = linkedLineItems.size;

                  if (availableCount === 1 && linkedCount === 0) {
                    return t("things.form.expenseLinking.foundOneLineItem");
                  } else if (availableCount === 0) {
                    return t("things.form.expenseLinking.allLinked", { count: lineItemsWithAccounts.length });
                  } else if (linkedCount > 0) {
                    return t("things.form.expenseLinking.itemsWithLinked", { total: lineItemsWithAccounts.length, available: availableCount, linked: linkedCount });
                  } else {
                    return t("things.form.expenseLinking.itemsAvailable", { count: lineItemsWithAccounts.length });
                  }
                })()}
              </p>
              <Select value={selectedLineItemId} onValueChange={onLineItemChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t("things.form.expenseLinking.chooseLineItem")} />
                </SelectTrigger>
                <SelectContent>
                  {lineItemsWithAccounts.map((lineItem) => {
                    const isLinked = linkedLineItems.has(lineItem.id);
                    return (
                      <SelectItem
                        key={lineItem.id}
                        value={lineItem.id}
                        disabled={isLinked}
                      >
                        <div className="flex items-center gap-2 py-1">
                          <span className={`font-medium ${isLinked ? 'text-muted-foreground' : ''}`}>
                            {showAccountNumbers && lineItem.expense_account?.account_number && (
                              <span className="font-mono text-muted-foreground">
                                {lineItem.expense_account.account_number}{' '}
                              </span>
                            )}
                            {lineItem.expense_account?.account_name || t("things.form.expenseLinking.unknownAccount")}
                          </span>
                          <span className="text-muted-foreground">•</span>
                          <span className={`text-sm ${isLinked ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                            {lineItem.description}
                          </span>
                          {isLinked && (
                            <>
                              <span className="text-muted-foreground">•</span>
                              <span className="text-xs text-destructive font-medium">{t("things.form.expenseLinking.alreadyLinked")}</span>
                            </>
                          )}
                          {!isLinked && (
                            <>
                              <span className="text-muted-foreground">•</span>
                              <span className="text-sm text-muted-foreground">
                                {t("things.form.expenseLinking.qty")} {lineItem.quantity} × <NumberDisplay value={lineItem.unit_price} className="inline" /> = <NumberDisplay value={lineItem.line_total || 0} className="inline font-semibold" />
                              </span>
                            </>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Create Expense Dialog */}
      {showCreateExpense && (
        <FullscreenFormDialog
          open={showCreateExpense}
          onOpenChange={setShowCreateExpense}
          title={t("things.form.expenseLinking.createNewExpense")}
          onSubmit={(e) => {
            e.preventDefault();
            expenseSubmitHandler?.();
          }}
          formId="asset-expense-form"
        >
          <CreateExpenseForm
            onSuccess={handleExpenseCreated}
            onSubmitReady={(handler) => setExpenseSubmitHandler(() => handler)}
          />
        </FullscreenFormDialog>
      )}
    </div>
  );
}
